/**
 * Collection List — ESM migration of collection-list.js (Phase 11).
 *
 * Picks a random subset of product cards from `.js-collection-list.can-refresh`
 * containers on page load, and animates a new random pick when the refresh
 * button is clicked. Runs on every turbo:load (initial load + Turbo navigation).
 *
 * A list inside a `data-turbo-permanent` element (the nav spotlight) is carried
 * across Turbo navigations with its pick intact, so it must only be picked once
 * — populateLists skips re-rolling a permanent list it has already populated.
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Sonnet 4.6
 * Updated: 2026-06-02 (Claude Opus 4.8) - Pick-once guard for data-turbo-permanent spotlights
 *
 * [CollectionList]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { slideUpOut, slideUpIn } from './core-anim.js';

const collectionListSelector = '.js-collection-list';
const canRefreshClass = 'can-refresh';
const canOverflowClass = 'can-overflow';
const refreshButtonSelector = '.js-refresh-list';
const draggingClass = 'dragging';

let easing;
let productCardSelector;

function init() {
  productCardSelector = appGet('dom.product.cardSelector');
  easing = appGet('easing');
  populateLists();
}

// ─── List Population ───────────────────────────────────────────────────────

function populateLists() {
  document.querySelectorAll(collectionListSelector + '.' + canRefreshClass).forEach((list) => {
    // A list inside a data-turbo-permanent element (the nav spotlight) is carried
    // across Turbo navigations with its pick intact. Pick it once and skip
    // re-rolling on later re-inits so the spotlight doesn't reshuffle on every
    // page change. Non-permanent lists are re-rendered fresh each navigation
    // (a new node with no marker), so they re-pick as before.
    const isPermanent = list.closest('[data-turbo-permanent]') != null;
    if (isPermanent && list.dataset.picked === 'true') return;
    list.dataset.picked = 'true';
    list.dataset.refreshCount = '0';
    runPick(list);
  });
}

function runPick(list) {
  const limit = parseInt(list.getAttribute('data-limit'), 10);
  const cards = Array.from(list.querySelectorAll(productCardSelector));
  const refreshCount = parseInt(list.dataset.refreshCount || '0', 10);

  log('[CollectionList] picking ' + limit + ' from ' + cards.length + ' cards');

  const picked = pickRandom(cards, limit);
  randomizeOrder(picked);
  list.dataset.refreshCount = String(refreshCount + 1);
  return picked;
}

// Picks `n` random elements from an array: hides the unpicked ones, returns
// the picked subset. Replaces the legacy $.fn.pick plugin (deleted in Phase 11).
function pickRandom(cards, n) {
  const indices = cards.map((_, i) => i);
  for (let k = indices.length - 1; k > 0; k--) {
    const j = Math.floor(Math.random() * (k + 1));
    [indices[k], indices[j]] = [indices[j], indices[k]];
  }
  const picked = new Set(indices.slice(0, n));
  cards.forEach((card, i) => {
    card.style.display = picked.has(i) ? 'block' : 'none';
  });
  return cards.filter((_, i) => picked.has(i));
}

// Fisher-Yates in-DOM shuffle: moves elements to random positions within
// their parent without rebuilding the list.
function randomizeOrder(cards) {
  if (cards.length < 2) return;
  const parent = cards[0].parentElement;
  if (!parent) return;
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    if (i !== j) parent.insertBefore(cards[i], cards[j]);
  }
}

// ─── Refresh ───────────────────────────────────────────────────────────────

function handleRefreshClick(event, target) {
  event.preventDefault();
  const list = target.closest(collectionListSelector);
  if (!list) return;

  const cards = Array.from(list.querySelectorAll(productCardSelector));
  const visibleCards = cards.filter((el) => el.offsetParent !== null);
  const cardParent = visibleCards[0]?.parentElement;
  if (!cardParent) return;

  const cardDuration = 300;

  cardParent.style.height = cardParent.offsetHeight + 'px';
  slideUpOut(visibleCards, { duration: cardDuration, easing, stagger: 50 }).then(() => {
    cardParent.style.opacity = '0';
    const newCards = runPick(list);
    cardParent.style.opacity = '1';
    slideUpIn(newCards, { duration: cardDuration, easing, stagger: 50, delay: 100 }).then(() => {
      cardParent.style.height = '';
    });
  });
}

// ─── Bootstrap ─────────────────────────────────────────────────────────────

document.addEventListener('click', (e) => {
  const btn = e.target.closest(refreshButtonSelector);
  if (btn) handleRefreshClick(e, btn);
});

// ep:ready fires after app hydration for the initial page load (see app-config.esm.js).
// turbo:load is used for subsequent Turbo Drive navigations — by the time this
// module registers the turbo:load listener, the initial turbo:load has already
// fired (Turbo dispatches it synchronously during its own module evaluation),
// so turbo:load reliably covers navigations only.
document.addEventListener('ep:ready', init);
document.addEventListener('turbo:load', init);

log('[CollectionList] loaded');
